"use client";
import config from "@/config/app";
import { initMercadoPago } from "@mercadopago/sdk-react";

declare global {
  interface Window {
    __mpInitializedOnce?: boolean;
    __mpInitError?: Error | null;
    MercadoPago?: unknown;
  }
}

// Initialize the Mercado Pago React SDK once (idempotent)
export function ensureMercadoPagoInitialized(): boolean {
  if (typeof window === "undefined") return false;
  const win = window as Window & {
    __mpInitializedOnce?: boolean;
    __mpInitError?: Error | null;
    MercadoPago?: unknown;
  };

  // Return cached result
  if (win.__mpInitializedOnce) return true;

  // Check if public key exists
  const publicKey = config.payments.mpPublicKey;
  if (!publicKey) {
    console.warn("[MP] Missing NEXT_PUBLIC_MP_PUBLIC_KEY");
    return false;
  }

  // Verify network connectivity before attempting initialization
  if (!navigator.onLine) {
    console.warn("[MP] Cannot initialize - device is offline");
    return false;
  }

  const locale: "pt-BR" | "en-US" | undefined =
    config.payments.mpLocale === "pt-BR"
      ? "pt-BR"
      : config.payments.mpLocale === "en-US"
      ? "en-US"
      : undefined;

  try {
    initMercadoPago(publicKey, { locale });
    win.__mpInitializedOnce = true;
    win.__mpInitError = null;
    return true;
  } catch (e) {
    const error = e instanceof Error ? e : new Error(String(e));

    // Check if it's a "already initialized" error (expected in dev)
    if (error.message?.includes("already") || win.MercadoPago) {
      console.warn("[MP] initMercadoPago already initialized", error.message);
      win.__mpInitializedOnce = true;
      win.__mpInitError = null;
      return true;
    }

    // Log other initialization errors without marking as initialized
    // This allows retry on next component mount
    console.error("[MP] Failed to initialize MercadoPago SDK:", error);
    win.__mpInitError = error;
    return false;
  }
}

/**
 * Check if MercadoPago SDK script is loaded in the DOM
 */
export function isMercadoPagoScriptLoaded(): boolean {
  if (typeof window === "undefined") return false;

  const scripts = document.querySelectorAll(
    'script[src*="sdk.mercadopago.com"]'
  );
  return scripts.length > 0;
}

/**
 * Get the last initialization error if any
 */
export function getMercadoPagoInitError(): Error | null {
  if (typeof window === "undefined") return null;
  return (
    (window as Window & { __mpInitError?: Error | null }).__mpInitError || null
  );
}
